import React, { useState, useRef } from 'react';
import { API_URL } from '../config/api';

const ImageUpload = ({ 
  value = '', 
  onChange, 
  placeholder = "Seleccionar imagen...",
  accept = "image/*",
  maxSize = 5 * 1024 * 1024, // 5MB
  className = ""
}) => {
  const [preview, setPreview] = useState(value);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError('');

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setError('Solo se permiten archivos de imagen');
      return;
    }

    // Validar tamaño
    if (file.size > maxSize) {
      setError(`El archivo es demasiado grande (máximo ${Math.round(maxSize / 1024 / 1024)}MB)`);
      return;
    }

    // Crear preview local
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Subir archivo al servidor
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/upload-logo`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al subir la imagen');
      }

      const result = await response.json();
      if (result.success) {
        setPreview(result.logo_url);
        onChange(result.logo_url);
      } else {
        throw new Error(result.detail || 'Error al procesar la imagen');
      }
    } catch (err) {
      setError(err.message);
      setPreview('');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setPreview('');
    onChange('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    if (!uploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`image-upload ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      
      {preview ? (
        <div className="image-preview">
          <img src={preview} alt="Preview" />
          <div className="image-actions">
            <button 
              type="button" 
              onClick={handleClick}
              disabled={uploading}
              className="btn-change"
            >
              {uploading ? 'Subiendo...' : 'Cambiar'}
            </button>
            <button 
              type="button" 
              onClick={handleRemoveImage}
              disabled={uploading}
              className="btn-remove"
            >
              Eliminar
            </button>
          </div>
        </div>
      ) : (
        <div 
          className="upload-placeholder"
          onClick={handleClick}
        >
          <div className="upload-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7,10 12,15 17,10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </div>
          <p>{uploading ? 'Subiendo imagen...' : placeholder}</p>
          <small>PNG, JPG, GIF hasta {Math.round(maxSize / 1024 / 1024)}MB</small>
        </div>
      )}

      {error && (
        <div className="upload-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
